/**
 * Flows to account for:
 * - User is on a chat page, chat gets loaded in SSR, needs to populate the store on client
 * - User submits prompt on homepage, needs to navigate to chat page once there's a chatId while keeping the streaming alive
 * - User navigates to a chat page, needs to fetch the chat from the server and populate the store on the client
 * - User navigates away from a chat page, needs to stop the streaming and clear the store
 * - User scrolls up in a chat, there's more messages to load, needs to fetch them and add to the store. UI needs to load them seamlessly while preserving the scroll position.
 */

import { type Params, useNavigate } from "@solidjs/router";
import { stAnalytics } from "@storytell/analytics";
import {
  type ChatPrompt,
  type Features,
  type LanguageRegions,
  type RichText,
  type Scope,
  newStreamRequest,
} from "@storytell/cp-client";
import { createInterruptChatRequest, useCallOptions, useChatClient, withBearerToken } from "@storytell/cp-client";
import { dispatchGlobalEvent } from "@storytell/events";
import {
  type StreamingBlock,
  type StreamingBlockId,
  type StreamingChatId,
  StreamingChatStatus,
  type StreamingMessage,
  type StreamingMessageId,
  type StreamingOperation,
  type StreamingOperationId,
  type StreamingService,
} from "@storytell/streaming";
import { createMemo, createRenderEffect, untrack } from "solid-js";
import { type SetStoreFunction, createStore } from "solid-js/store";
import { urls } from "~/lib/urls";
import { createChatServiceResource } from "./chatServiceResource";
import { createChatServiceScope } from "./chatServiceScope";

export type ChatService = ReturnType<typeof createChatService>;

export type ChatServiceState = {
  chatId: StreamingChatId | undefined;
  error: Error | undefined;
  isStreaming: boolean;
  currentStreamingMessageId: StreamingMessageId | undefined;
  isInterrupting: boolean;
};
export type SetChatServiceState = SetStoreFunction<ChatServiceState>;

/**
 * High-level manager for handling chat functionality.
 * Resposible for:
 * - Navigation between chats
 * - Chat creation
 * - Chat playback
 * - UI state management
 *
 * The logic for storing and streaming chats is handled by the streaming package.
 * This service wraps extra logic around it.
 */
export const createChatService = (deps: {
  urlParams: () => Params;
  authReady: () => boolean;
  authToken: () => string | undefined;
  projectId: () => string | undefined;
  stream: StreamingService;
  reloadBillingInfo: () => void;
  generateChatTitle?: (chatId: StreamingChatId, message: StreamingMessage, model?: Features.Feature) => Promise<void>;
  setChatTitle?: (chatId: StreamingChatId, title: string) => void;
}) => {
  const navigate = useNavigate();

  const [state, setState] = createStore<ChatServiceState>({
    chatId: deps.urlParams().chatId as StreamingChatId | undefined,
    error: undefined,
    isStreaming: false,
    currentStreamingMessageId: undefined,
    isInterrupting: false,
  });

  const resource = createChatServiceResource({
    authReady: deps.authReady,
    authToken: deps.authToken,
    urlParams: deps.urlParams,
    state,
    setState,
    stream: deps.stream,
  });

  const scope = createChatServiceScope({
    projectId: () => deps.projectId(),
  });

  /**
   * Ensures that whenever a new chat is loaded, we process it to populate the store
   * It's the store that the UI pulls data from, not the resource directly
   */
  createRenderEffect(() => {
    if (resource.latest?.data) {
      untrack(() => {
        if (resource.latest?.data) {
          deps.stream.chat.processChatRecording(resource.latest.data);
          // Set the chat title from the backend data
          if (deps.setChatTitle && resource.latest.data.title) {
            deps.setChatTitle(resource.latest.data.chatId as StreamingChatId, resource.latest.data.title);
          }
        }
      });
    }
  });

  /**
   * Main handler for submitting a prompt
   */
  const startOrContinueChat = (opts: {
    submission: RichText.RichText;
    locale: LanguageRegions.LanguageRegion;
    projectId: string;
    workspaceId: string;
    scope: Scope.Scope;
    model?: Features.Feature;
    executionMode?: ChatPrompt.ExecutionMode;
  }) => {
    const chat = getChat();
    const chatId = chat?.id;

    const parentMessageId = chat?.messages[0];

    setState("isStreaming", true);

    const request = newStreamRequest(
      chatId || "",
      opts.submission,
      opts.locale,
      false,
      opts.projectId,
      opts.workspaceId,
      opts.scope,
      parentMessageId,
      opts.model,
      opts.executionMode,
    );
    const token = deps.authToken();

    let capturedOnce = false;
    const onChatIdCaptured = (chatId: StreamingChatId) => {
      if (capturedOnce) return;
      dispatchGlobalEvent("new-chat-created", {
        id: chatId,
        title: "New Chat",
      });
      dispatchGlobalEvent("prompt-submitted-successfuly", undefined);
      capturedOnce = true;
      setState({
        chatId,
        error: undefined,
      });
      navigate(urls.chatPage(chatId));
    };

    const onMessageStarted = (messageId: StreamingMessageId) => {
      setState("currentStreamingMessageId", messageId);
    };

    deps.stream.chat.startStreamingChat({
      request: request,
      options: {
        bearerToken: token,
      },
      onChatIdCaptured,
      onMessageStarted,
      onMessageFinished: async (messageId) => {
        setState("isStreaming", false);
        setState("currentStreamingMessageId", undefined);
        deps.reloadBillingInfo();

        try {
          const message = deps.stream.message.get(messageId);
          if (!message) return;
          const cost = message.aggregateMetrics?.totalCredits ?? 0;
          stAnalytics.track("credits_consumed", {
            reason: "stream_response",
            amount: cost,
          });

          // Generate title for the chat if this is the first message
          const chat = getChat();
          if (chat && deps.generateChatTitle && chat.messages.length === 1) {
            // Generate title asynchronously
            deps.generateChatTitle(chat.id, message, opts.model).catch((err) => {
              console.error("Failed to generate chat title:", err);
            });
          }
        } catch (err) {}
      },
    });
  };

  const getChatId = () => state.chatId;
  const getChat = createMemo(() => (state.chatId ? deps.stream.state.chats[state.chatId] : undefined));
  const getChatError = () => state.error || resource()?.error;
  const isChatStreaming = () =>
    state.isStreaming || (state.chatId && deps.stream.chat.getStatus(state.chatId) === StreamingChatStatus.GENERATING);
  const isChatIdle = () => !isChatStreaming();
  const isChatLoading = () => resource.loading;
  const isInterrupting = () => state.isInterrupting;

  /**
   * Set the scope from the last message when loading a chat
   * This runs after the chat messages have been processed and populated
   */
  createRenderEffect(() => {
    const chat = getChat();
    if (chat && chat.messages.length > 0) {
      const lastMessageId = chat.messages[chat.messages.length - 1];
      const lastMessage = deps.stream.state.messages[lastMessageId];
      const messageScope = lastMessage?.prompt.scope;
      if (messageScope) {
        untrack(() => {
          scope.loadScopeFromMessage(messageScope);
        });
      }
    }
  });

  const shouldShowContent = () => {
    if (getChatId()) return true;
    if (isChatStreaming()) return true;
    if (isChatLoading()) return true;

    return false;
  };

  const getOperation = (operationId: StreamingOperationId) => deps.stream.operation.get(operationId);
  const getMessageOperations = (messageId: StreamingMessageId) =>
    deps.stream.message
      .get(messageId)
      ?.operations.map((op) => deps.stream.operation.get(op))
      .filter((op): op is StreamingOperation => op !== undefined);
  const getMessage = (messageId: StreamingMessageId) => deps.stream.message.get(messageId);
  const getChatMessages = () =>
    getChatId()
      ? deps.stream.chat
          .get(getChatId()!)
          ?.messages.map((m) => deps.stream.message.get(m))
          .filter((m): m is StreamingMessage => m !== undefined)
      : undefined;
  const getBlock = (blockId: StreamingBlockId) => deps.stream.block.get(blockId);
  const getOperationBlocks = (operationId: StreamingOperationId) =>
    deps.stream.operation
      .get(operationId)
      ?.blocks.map((b) => deps.stream.block.get(b))
      .filter((b): b is StreamingBlock => b !== undefined);
  const getOperationTask = (operationId: StreamingOperationId) => deps.stream.operation.get(operationId)?.task;

  const interruptStream = async (userInput = "") => {
    const messageId = state.currentStreamingMessageId;
    if (!messageId) {
      console.warn("No active streaming message to interrupt");
      return;
    }

    if (state.isInterrupting) {
      console.warn("Interrupt already in progress");
      return;
    }

    const token = deps.authToken();
    if (!token) {
      console.error("No auth token available for interrupt request");
      return;
    }

    try {
      setState("isInterrupting", true);

      const chatClient = useChatClient();
      const request = createInterruptChatRequest({
        messageId: messageId,
      });

      const result = await chatClient.interruptChat(request, useCallOptions(withBearerToken(token)));

      if (!result.isOk()) {
        console.error("Failed to interrupt chat:", result.error);
        throw new Error(`Failed to interrupt chat: ${result.error.message}`);
      }

      if (result.value.success) {
        console.log("Successfully sent interrupt signal to backend");

        // Get the message ID before clearing it
        const interruptedMessageId = state.currentStreamingMessageId;

        // Clean up streaming state after successful interruption
        setState("isStreaming", false);
        setState("currentStreamingMessageId", undefined);

        // Since the backend doesn't send onMessageFinished when interrupted,
        // we need to manually clean up the UI state
        if (interruptedMessageId && state.chatId) {
          // Force the streaming store to update by dispatching a custom event
          // This ensures the UI reflects that streaming has stopped
          deps.reloadBillingInfo();

          // Log metrics for the interrupted message if available
          try {
            const message = deps.stream.message.get(interruptedMessageId);
            if (message) {
              const cost = message.aggregateMetrics?.totalCredits ?? 0;
              stAnalytics.track("credits_consumed", {
                reason: "stream_response",
                amount: cost,
              });
            }
          } catch (err) {
            console.error("Failed to track interrupted message metrics:", err);
          }
        }
      } else {
        console.warn("Interrupt request was not successful");
        throw new Error("Failed to interrupt chat: Request was not successful");
      }
    } catch (error) {
      console.error("Failed to interrupt stream:", error);
      throw error;
    } finally {
      setState("isInterrupting", false);
    }
  };

  const resetChat = () => {
    setState({
      chatId: undefined,
      error: undefined,
      isStreaming: false,
      currentStreamingMessageId: undefined,
      isInterrupting: false,
    });
  };

  return {
    scope,
    getChatId,
    shouldShowContent,
    getChat,
    getChatError,
    isChatStreaming,
    isChatIdle,
    isChatLoading,
    isInterrupting,
    startOrContinueChat,
    interruptStream,
    resetChat,
    getOperation,
    getMessageOperations,
    getMessage,
    getChatMessages,
    getBlock,
    getOperationBlocks,
    getOperationTask,
  };
};
